import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../../components/common/Loader";
import AddHomePageModal from "../../components/modals/AddHomePageModal";
import EditHomePageModal from "../../components/modals/EditHomePageModal";
import { FaEdit, FaTrash } from "react-icons/fa";
import WelcomeCard from "../../components/common/WelcomeCard";

const HomePage = () => {
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  const fetchHomePageContent = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/home/get-sections`);
      const data = Array.isArray(res.data.data) ? res.data.data : [];
      setContentList(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load homepage content");
      toast.error("Failed to load homepage content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomePageContent();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this content?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/home/delete-section/${id}`);
      setContentList(contentList.filter(c => c._id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete content");
    }
  };

  const handleContentAdded = (newContent) => {
    if (!newContent) return;
    setContentList(prev => [newContent, ...prev]);
  };

  const handleContentUpdated = (updatedContent) => {
    if (!updatedContent) return;
    setContentList(prev => prev.map(c => c._id === updatedContent._id ? updatedContent : c));
    toast.success("Updated successfully");
  };

  return (
    <div className="p-4">
      <WelcomeCard />

      {/* Add/Edit Modals */}
      <AddHomePageModal
        isOpen={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onContentAdded={handleContentAdded}
      />

      {selectedContent && (
        <EditHomePageModal
          isOpen={openEditModal}
          onClose={() => setOpenEditModal(false)}
          content={selectedContent}
          onContentUpdated={handleContentUpdated}
        />
      )}

      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">Home Page Content</h1>
        {/* Uncomment to enable Add Content */}
        <button
          onClick={() => setOpenAddModal(true)}
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 flex items-center gap-2"
        >
          + Add Content
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center mt-10">
          <Loader />
        </div>
      ) : contentList.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">No content found</div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {contentList.map(content => (
            <div key={content._id} className="border p-4 rounded shadow relative">
              <div className="grid grid-cols-2">
                <div>
                  <h2 className="font-bold text-lg">{content.heading}</h2>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => { setSelectedContent(content); setOpenEditModal(true); }}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <FaEdit size={20} />
                  </button>
                  {/* Uncomment to enable Delete */}
                  {/* <button
                    onClick={() => handleDelete(content._id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FaTrash size={20} />
                  </button> */}
                </div>
              </div>

              <h3 className="text-orange-500">{content.subheading}</h3>

              {/* Description */}
              {Array.isArray(content.description) && content.description.length > 0 && content.description.map((d, i) => (
                <div key={i} className="text-gray-600" dangerouslySetInnerHTML={{ __html: d }} />
              ))}

              {/* Images */}
              {Array.isArray(content.images) && content.images.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {content.images.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={`homepage-${i}`}
                      className="w-full md:w-1/2 h-auto object-cover rounded"
                    />
                  ))}
                </div>
              )}

              {/* Key Points */}
              {Array.isArray(content.keyPoints) && content.keyPoints.length > 0 && (
                <>
                  <h4 className="mt-2 font-semibold">Key Points</h4>
                  <ul className="list-disc ml-4">
                    {content.keyPoints.map((kp, i) => (
                      <li key={i}>
                        <strong>{kp.title}:</strong>{" "}
                        <span dangerouslySetInnerHTML={{ __html: kp.description }} />
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HomePage;
